// ABOUTME: The dealership choice context and its hooks, apart from the provider so the pickers it renders can read it.
// ABOUTME: DealershipChoiceProvider (DealershipChoiceContext.tsx) fills it; components read it with the hooks below.

import { createContext, useContext } from 'react';

import type { DealershipChoice, DealershipPickerOptions, SelectableDealerships } from '../types';

export type DealershipChoiceContextValue = {
    /**
     * the dealership the current screen (a catalogue, an offer) works in, sent with its requests;
     * undefined on every other screen, so nothing created there gets a dealership
     */
    requestDealership: string | undefined;
    /** the dealership of the cart: its select, the user's last choice in this tab */
    cartDealership: string | undefined;
    /** what the user last picked in this tab (catalogue or cart): a dealership or "none"; undefined before any pick */
    lastChoice: DealershipChoice | undefined;
    /** sets the dealership of the current screen, without changing the user's last choice */
    setRequestDealership: (dealershipId: string | undefined) => void;
    /** a choice made on the catalogue: the catalogue's dealership, the cart's and the last choice */
    chooseRequestDealership: (dealershipId: string | undefined) => void;
    /** a choice made in the cart: the cart's dealership and the last choice, and the catalogue's when one is open */
    chooseCartDealership: (dealershipId: string | undefined) => void;
    /**
     * a catalogue screen mounts (true) or unmounts (false); once the last mounted one leaves, the
     * dealership is cleared
     */
    setCatalogActive: (active: boolean) => void;
    /** a screen that set its own dealership (offer) gives it back: no dealership again */
    releaseRequestDealership: () => void;
    /**
     * the dealerships the user may pick, loaded once and cached; rejects when the request fails (the
     * next call retries), so a failure is never taken for "no dealerships"
     */
    loadSelectable: () => Promise<SelectableDealerships>;
    /** opens the picker on the given choice; resolves with the choice, or undefined when cancelled */
    chooseDealership: (
        initialChoice?: DealershipChoice,
        options?: DealershipPickerOptions,
    ) => Promise<DealershipChoice | undefined>;
    /** opens Assign → Dealership on a node; resolves true once it is assigned, false when closed */
    assignNodeDealership: (hierarchyId: string, nodeId: string) => Promise<boolean>;
};

export const DealershipChoiceContext = createContext<DealershipChoiceContextValue | null>(null);

/** The dealership choice of the current screen, or null outside a DealershipChoiceProvider. */
export function useOptionalDealershipChoice(): DealershipChoiceContextValue | null {
    return useContext(DealershipChoiceContext);
}

/** The dealership choice of the current screen; throws outside a DealershipChoiceProvider. */
export function useDealershipChoice(): DealershipChoiceContextValue {
    const context = useContext(DealershipChoiceContext);
    if (!context) {
        throw new Error('useDealershipChoice must be used inside a DealershipChoiceProvider');
    }
    return context;
}
